import React, { useEffect } from 'react';
import _ from 'lodash';
import KogAutoCompleteControlled from '@/main/components/shared/inputAutoComplete/autoCompleteControlled';
import KogInputTextControlledNew from '@/main/components/shared/inputText/inputTextControlledNew';
import { StatusRegisterEnum } from '@/main/services/enum/StatusRegisterEnum';
import { getTranslateByKey } from '@/main/services/utils';
import * as S from '../styles';
import { SaveValuatorRequest } from '@/main/models/request/valuatorRequest';
import { useFormContext } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import useAsyncModalConfirmReason from '@/main/components/shared/asyncModalConfirmReason/asyncModalConfirmReason';
import { toBase64 } from '@/main/services/utilsFile';
import {
  getStatusRegister,
  useStatusRegisterReducer
} from '@/main/store/statusRegister';
import ContactInfo, {
  formattedContactSave,
  IContactInfoState
} from '@/main/components/shared/contactInfo';
import AddressInfo, {
  IAddressInfoState
} from '@/main/components/shared/addressInfo';
import {
  ManufacturerRequest,
  DistributionRegionFilterRequest,
  ReasonFilterRequest,
  AddressSaveRequest
} from '@/main/models/request';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { getDistributionRegion } from '@/main/store/distributionRegion';
import { getReason } from '@/main/store/reason';
import { getSupplierType } from '@/main/store/supplierType';
import { getManufacturers } from '@/main/store/vehicle/manufacturer';
import {
  IValuatorRegisterForm,
  useValuatorRegisterContext
} from './valuatorRegisterContext';
import { KOGAreaButtons } from '@/main/components/shared/styles';
import Button from '@/main/components/shared/button/button';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import { CustomError } from '@/main/services/class/customError';
import { toast } from 'react-toastify';
import { Guid } from 'guid-typescript';
import { valuatorApi } from '@/main/api';
import { RoutesConstants } from '@/main/routes/routesConstants';
import InsurerCompany from '@/main/components/shared/insurerCompanyValuator';
import { InsurerByValuatorResponse } from '@/main/models/response/valuatorResponse';
import { Box } from '@mui/material';
import { useUserReducer } from '@/main/store/user';
import { CompanyTypeEnum } from '@/main/services/enum/CompanyTypeEnum';

interface ITabValuatorRegister {
  dataContacts: IContactInfoState[];
  initialDataContacts: IContactInfoState[];
  dataAddress: IAddressInfoState[];
  initialDataAddress: IAddressInfoState[];
  dataInsurers: InsurerByValuatorResponse[];
  setDataAddress: React.Dispatch<React.SetStateAction<IAddressInfoState[]>>;
  setDataContacts: React.Dispatch<React.SetStateAction<IContactInfoState[]>>;
  loadInsurersByValuator: (guid: string, guidInsurer: string) => void;
  loadInfoValuator: (guid: string, guidInsurer: string) => void;
}

export default function TabValuatorRegister({
  dataContacts,
  initialDataContacts,
  dataAddress,
  initialDataAddress,
  dataInsurers,
  setDataAddress,
  setDataContacts,
  loadInsurersByValuator,
  loadInfoValuator
}: ITabValuatorRegister) {
  const methods = useFormContext<IValuatorRegisterForm>();
  const watcherCountry = methods.watch('country');
  const watcherStatusRegister = methods.watch('statusRegister');

  const [getConfirmationReason, ModalConfirmationReason] =
    useAsyncModalConfirmReason();

  const [searchParams, setSearchParams] = useSearchParams();
  const guidValuatorParams = searchParams.get('valuatorGuid');

  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { countriesUser } = useCountryReducer();
  const { statusRegister } = useStatusRegisterReducer();
  const { isEdit, setIsEdit } = useValuatorRegisterContext();
  const userReducer = useUserReducer();

  const guidInsurer =
    userReducer.currentUser?.idCompanyType == CompanyTypeEnum.Insurer
      ? userReducer.currentUser?.guidCompany
      : null;

  const {
    control,
    setValue,
    formState: { errors }
  } = useFormContext();

  useEffect(() => {
    dispatch(getCountriesUser());
    dispatch(getStatusRegister());
  }, []);

  useEffect(() => {
    if (countriesUser.data?.length === 1) {
      const countryValue = countriesUser.data[0];
      setValue('country', countryValue);
      methods.setValue('country', countryValue);
    }
  }, [countriesUser.data?.length]);

  useEffect(() => {
    if (!isEdit && statusRegister?.data && statusRegister.data?.length > 0) {
      const activeStatus = statusRegister?.data.find(
        x => String(x.key) === String(StatusRegisterEnum.Active)
      );
      if (activeStatus && !methods.getValues('statusRegister')?.key) {
        setValue('statusRegister', activeStatus);
        methods.setValue('statusRegister', activeStatus);
      }
    }
  }, [isEdit, statusRegister.data]);

  useEffect(() => {
    if (watcherCountry !== null) {
      dispatch(getSupplierType());

      const requestMake: ManufacturerRequest = {
        guidCountry: String(watcherCountry?.key)
      };
      dispatch(getManufacturers(requestMake));

      const requestRegion: DistributionRegionFilterRequest = {
        guidCountry: String(watcherCountry?.key)
      };
      dispatch(getDistributionRegion(requestRegion));

      const requestReason: ReasonFilterRequest = {
        guidCountry: String(watcherCountry?.key),
        idReasonType: ReasonTypeEnum.Blacklist
      };
      dispatch(getReason(requestReason));
    }
  }, [watcherCountry]);

  const isFormChanged = methods.formState.isDirty;
  const isContactsChanged = !_.isEqual(initialDataContacts, dataContacts);
  const isAddressChanged = !_.isEqual(initialDataAddress, dataAddress);
  const hasChanges = isFormChanged || isContactsChanged || isAddressChanged;

  const isDisabledCountry = () => {
    return countriesUser.data?.length === 1;
  };

  function handleBack() {
    navigate(RoutesConstants.registrationSearchValuator);
  }

  function validateForm() {
    const messages = [];

    const valuatorName = methods.getValues('name');
    const valuatorStatus = methods.getValues('statusRegister');

    if (!valuatorName)
      messages.push(getTranslateByKey('information.nameRequired', false));

    if (!valuatorStatus?.key)
      messages.push(getTranslateByKey('information.statusRequired', true));

    if (dataContacts.length === 0)
      messages.push(
        getTranslateByKey('information.mustHaveAtLeastOneContact', true)
      );

    if (dataContacts.length > 0) {
      const primaryContact = dataContacts.find(
        contact => contact.isPrimaryContact
      );

      if (!primaryContact)
        messages.push(
          getTranslateByKey(
            'information.mustHaveAtLeastOnePrimaryContact',
            true
          )
        );
    }

    if (messages.length > 0) {
      messages.map(message => toast.error(message));
      throw new CustomError(messages.join('/n'), 'MISSING_FIELDS');
    }
  }

  const formatDataAddress = (
    addresses: IAddressInfoState[],
    isEditAddress = false
  ) => {
    return addresses.map<AddressSaveRequest>(address => ({
      ...(isEditAddress && {
        guid: Guid.isGuid(address.id) ? address.id : Guid.EMPTY
      }),
      idAddressType: address.addressType.key as number,
      street: address.address,
      complement: address.complement,
      number: address.number,
      municipality: {
        guid: address.municipality.key.toString()
      }
    }));
  };

  async function handleSubmitEditValuator(data: IValuatorRegisterForm) {
    try {
      validateForm();

      const isFormChanged = methods.formState.isDirty;
      const isContactsChanged = !_.isEqual(initialDataContacts, dataContacts);
      const isAddressChanged = !_.isEqual(initialDataAddress, dataAddress);

      if (!isFormChanged && !isContactsChanged && !isAddressChanged) {
        return;
      }

      const resultConfirm = await getConfirmationReason(
        getTranslateByKey('information.confirmChangeTitle'),
        ReasonTypeEnum.ChangeCompany
      );

      if (!resultConfirm.confirmed) return;

      const formattedContacts = formattedContactSave(dataContacts, true);
      const formattedAddress = formatDataAddress(dataAddress, true);

      let filesRequest = undefined;
      if (resultConfirm.files && resultConfirm.files.length > 0) {
        filesRequest = await Promise.all(
          resultConfirm.files.map(async item => {
            const base64DataUrl = await toBase64(item);
            return {
              fileName: item.name,
              contentType: item.type,
              fileBase64: base64DataUrl as string
            };
          })
        );
      }

      const request: SaveValuatorRequest = {
        guid: String(guidValuatorParams),
        name: String(data.name),
        registrationNumber: String(data.rfc),
        idStatusRegister: Number(data.statusRegister?.key),
        guidCountry: String(data.country?.key),
        addresses: formattedAddress,
        contacts: formattedContacts,
        insurerIds: [],
        supplierCompanyRelations: [],
        guidInsurerLog:
          userReducer.currentUser?.idCompanyType == CompanyTypeEnum.Insurer
            ? userReducer.currentUser?.guidCompany
            : null,
        changeJustification: {
          guidReason: resultConfirm.reason
            ? String(resultConfirm.reason.key)
            : null,
          reasonDescription: resultConfirm.observation || null,
          filesUpload: filesRequest
        }
      };

      valuatorApi.save(request).then(response => {
        const { result } = response.data;
        toast.success(
          getTranslateByKey('information.valuatorEditSuccessfully')
        );
        setIsEdit(true);
        setSearchParams({ valuatorGuid: result.guid }, { replace: true });
        loadInfoValuator(result.guid, guidInsurer ?? '');
      });
    } catch (error) { }
  }

  async function handleSubmitRegisterValuator(data: IValuatorRegisterForm) {
    try {
      validateForm();
      const formattedContacts = formattedContactSave(dataContacts);
      const formattedAddress = formatDataAddress(dataAddress, true);

      const request: SaveValuatorRequest = {
        name: String(data.name),
        registrationNumber: String(data.rfc),
        idStatusRegister: Number(data.statusRegister?.key),
        guidCountry: String(data.country?.key),
        addresses: formattedAddress,
        contacts: formattedContacts,
        insurerIds: [],
        supplierCompanyRelations: [],
        guidInsurerLog:
          userReducer.currentUser?.idCompanyType == CompanyTypeEnum.Insurer
            ? userReducer.currentUser?.guidCompany
            : null
      };

      valuatorApi.save(request).then(response => {
        const { result } = response.data;
        toast.success(
          getTranslateByKey('information.valuatorSavedSuccessfully')
        );
        setIsEdit(true);
        setSearchParams({ valuatorGuid: result.guid }, { replace: true });
      });
    } catch (error) { }
  }

  return (
    <>
      <Box style={{ marginBottom: '2rem' }}>
        <S.RowInputs>
          <KogAutoCompleteControlled
            label={getTranslateByKey('information.country')}
            control={control}
            nameCustom="country"
            rulesCustom={{ required: true }}
            errorMessageCustom={
              errors.country?.type === 'required'
                ? getTranslateByKey('information.countryRequired', true)
                : ''
            }
            hasErrorCustom={!!errors.country}
            options={countriesUser.data ?? []}
            isLoading={countriesUser.isLoading}
            onChangeCustom={() => { }}
            disabled={isDisabledCountry()}
            autoHighlightCustom={countriesUser.data?.length === 1}
            setValueCustom={setValue}
            id="id-registerValuator-txt-country"
          />

          <KogInputTextControlledNew
            name="name"
            label={getTranslateByKey('information.name')}
            control={control}
            controllerProps={{ rules: { required: true } }}
            errorMessageCustom={
              errors.name?.type === 'required'
                ? getTranslateByKey('information.nameRequired', false)
                : ''
            }
            id="id-registerValuator-txt-name"
          />
          <KogInputTextControlledNew
            label={getTranslateByKey('information.ID')}
            helperText={''}
            name="rfc"
            error={!!errors.rfc}
            inputProps={{}}
            errorMessageCustom={
              errors.rfc?.type === 'required'
                ? getTranslateByKey('information.idRequired', true)
                : ''
            }
            controllerProps={{
              rules: {
                required: true
              }
            }}
            control={control}
            id="id-registerValuator-txt-rfc"
          />
          <KogAutoCompleteControlled
            label={getTranslateByKey('information.status')}
            nameCustom="statusRegister"
            control={control}
            setValueCustom={setValue}
            options={statusRegister.data}
            isLoading={statusRegister.isLoading}
            rulesCustom={{ required: true }}
            onChangeCustom={() => { }}
            id="id-registerValuator-txt-statusRegister"
            error={!!errors.statusRegister}
            errorMessageCustom={
              errors.statusRegister?.type === 'required'
                ? getTranslateByKey('information.statusRequired', true)
                : ''
            }
          />
        </S.RowInputs>
      </Box>

      <ContactInfo
        dataContacts={dataContacts}
        setDataContacts={setDataContacts}
        typeCompany=""
        watcherCountry={watcherCountry}
        companyType={CompanyTypeEnum.Valuator}
        companyName={methods.watch('name')}
      />

      <AddressInfo
        disableDeliveryAddress
        dataAddress={dataAddress}
        setDataAddress={setDataAddress}
        watcherCountry={watcherCountry}
        typeCompany=""
      />

      {isEdit && (
        <InsurerCompany
          dataInsurerCompany={dataInsurers}
          guidValuator={guidValuatorParams as string}
          refreshData={() =>
            loadInsurersByValuator(guidValuatorParams ?? '', guidInsurer ?? '')
          }
        />
      )}

      <S.ContainerButton>
        <KOGAreaButtons $isRegisterScreen>
          <SecondaryButton
            type="button"
            onClick={handleBack}
            id="id-registerValuator-btn-back">
            {getTranslateByKey('information.back')}
          </SecondaryButton>
          <Button
            type="submit"
            id="id-registerValuator-btn-save"
            disabled={!hasChanges}
            onClick={methods.handleSubmit(
              isEdit ? handleSubmitEditValuator : handleSubmitRegisterValuator
            )}>
            {getTranslateByKey('information.save')}
          </Button>
        </KOGAreaButtons>
      </S.ContainerButton>
      <ModalConfirmationReason closeByOverlay={false} />
    </>
  );
}
